import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Badge,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api";
import { FaCheckCircle, FaUserCircle, FaMapMarkerAlt } from "react-icons/fa";

const ADMIN_EMAILS = [
  "prateekmishrachs@gmail.com",
  "prateekmishrachs@gmail..com",
];

const formatCategory = (value) =>
  value
    ? value
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "";

export default function AdminVerifyWorkers() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [verifyingId, setVerifyingId] = useState("");
  const [verifiedBy, setVerifiedBy] = useState("");

  const isAdmin = useMemo(() => {
    const email = user?.email ? user.email.toLowerCase().trim() : "";
    return ADMIN_EMAILS.includes(email);
  }, [user?.email]);

  const fetchWorkers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/workers/admin/list", {
        params: { adminEmail: user?.email },
      });
      if (res.data?.success) {
        setWorkers(res.data.workers || []);
      } else {
        setError(res.data?.message || "Failed to load workers.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to load workers."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && user?.email) {
      fetchWorkers();
    }
  }, [isAdmin, user?.email]);

  const handleVerify = async (workerId, verified) => {
    if (!workerId || !user?.email) return;
    setVerifyingId(workerId);
    setMessage("");
    setError("");
    try {
      const res = await api.patch(`/workers/${workerId}/verify`, {
        adminEmail: user.email,
        verified,
        verifiedBy: verifiedBy.trim() || undefined,
      });
      if (res.data?.success && res.data?.worker) {
        setWorkers((prev) =>
          prev.map((w) =>
            String(w._id) === String(workerId) ? res.data.worker : w
          )
        );
        setMessage(
          verified
            ? "Worker marked as verified."
            : "Verification removed."
        );
      } else {
        setError(res.data?.message || "Failed to update verification.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update verification."
      );
    } finally {
      setVerifyingId("");
    }
  };

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Card
          className="shadow border-0 rounded-4 mx-auto"
          style={{ maxWidth: 620 }}
        >
          <Card.Body className="p-4 text-center">
            <h3 className="fw-bold text-danger mb-3">Access restricted</h3>
            <p className="text-muted mb-4">
              Only authorized employees can verify workers. Please contact
              support if you believe this is an error.
            </p>
            <Button variant="primary" onClick={() => navigate("/")}>
              Return to home
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card className="shadow border-0 rounded-4 mb-4">
        <Card.Body className="p-4">
          <h2 className="fw-bold text-primary mb-2">Verify Workers</h2>
          <p className="text-muted mb-4">
            After a worker completes skill verification (videocall or in-person),
            mark them as verified. Their profile will then show a &quot;Verified&quot; badge
            to customers.
          </p>
          <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
            <Form.Label className="small text-muted">
              Your name (optional, for &quot;Verified by&quot;)
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Priya from Rentalo"
              value={verifiedBy}
              onChange={(e) => setVerifiedBy(e.target.value)}
            />
          </Form.Group>
          {error && <Alert variant="danger">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate("/admin/ads")}
          >
            Back to Ads
          </Button>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : workers.length === 0 ? (
        <Card className="shadow border-0 rounded-4">
          <Card.Body className="text-center py-5 text-muted">
            No workers found.
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          {workers.map((worker) => {
            const username =
              worker.userId?.username || "Unknown";
            const isVerifying = String(verifyingId) === String(worker._id);
            return (
              <Col key={worker._id} md={6} lg={4}>
                <Card className="h-100 shadow-sm border-0 rounded-4">
                  <Card.Body>
                    <div className="d-flex align-items-start mb-2">
                      <div
                        className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: 48, height: 48 }}
                      >
                        <FaUserCircle size={24} className="text-primary" />
                      </div>
                      <div className="flex-grow-1">
                        <span className="fw-bold">{username}</span>
                        {worker.verified && (
                          <Badge bg="success" className="ms-2">
                            <FaCheckCircle className="me-1" /> Verified
                          </Badge>
                        )}
                        <div className="small text-muted mt-1">
                          <FaMapMarkerAlt className="me-1" />
                          {worker.city}, {worker.state}
                        </div>
                        <Badge bg="secondary" className="mt-1">
                          {formatCategory(worker.category)}
                        </Badge>
                      </div>
                    </div>
                    <div className="small text-muted mb-3">
                      {worker.userId?.phone && (
                        <span className="d-block">Phone: {worker.userId.phone}</span>
                      )}
                      {worker.userId?.email && (
                        <span className="d-block">Email: {worker.userId.email}</span>
                      )}
                      {worker.verifiedBy && (
                        <span className="d-block text-success">
                          Verified by: {worker.verifiedBy}
                        </span>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      {worker.verified ? (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          disabled={isVerifying}
                          onClick={() => handleVerify(worker._id, false)}
                        >
                          {isVerifying ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Remove verification"
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="success"
                          disabled={isVerifying}
                          onClick={() => handleVerify(worker._id, true)}
                        >
                          {isVerifying ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <FaCheckCircle className="me-1" /> Mark verified
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
}
